<script setup lang="ts">
import CourseCard from '../../components/CourseCard/CourseCard.vue';
import { mdiFileDocumentArrowRightOutline } from '@quasar/extras/mdi-v7';
import { useCourseStore } from './useCourseStore';
import { inject, onMounted, onUnmounted, watchEffect } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';
import { useRouter } from 'vue-router';
import { useUserCourseData } from '../../useUserCourseData';
import { useGlobalStore } from 'src/stores/global';
import { usePremiumAccessGuard } from '../../composables/usePremiumAccessGuard';
import BButton from 'src/shared/components/base/BButton/BButton.vue';

const globalStore = useGlobalStore();
const router = useRouter();
const { handlePremiumAccessDenied } = usePremiumAccessGuard();

const {
  courseStorePagination,
  courses,
  assignCourseToUser,
  fetchCourseStore,
  isOwnedCourse,
  getUserCourseByCourse,
} = useCourseStore();

const { getCourseCardHeaderBg, courseCardHeaderBgData } = useUserCourseData();

watchEffect(() => {
  globalStore.setTotal(courseStorePagination.value?.total || 0);
});
onMounted(async () => {
  globalStore.setTotal(courseStorePagination.value?.total || 0);
  if (courseCardHeaderBgData.value?.url) return;
  await getCourseCardHeaderBg();
});

onUnmounted(() => {
  globalStore.setTotal(undefined);
});

const currentUserId = inject('currentUserId') as ComputedRef<number>;
const invalidLicense = inject('invalidLicense') as Ref<boolean>;
const $q = useQuasar();

function isCourseLocked(isFree?: boolean | null): boolean {
  return !!invalidLicense.value && !isFree;
}

async function handleActionClick(courseId: number) {
  const selectedCourse = courses.value.find((course) => course.id === courseId);
  if (isCourseLocked(selectedCourse?.is_free)) {
    $q.notify({
      type: 'warning',
      message: t('LICENSE_REQUIRED_FOR_PREMIUM'),
    });
    return;
  }

  console.log('Action clicked', courseId);
  const { success, data, error } = await assignCourseToUser(
    courseId,
    currentUserId.value,
  );

  if (!success) {
    if (handlePremiumAccessDenied(error)) {
      return;
    }

    $q.notify({
      type: 'negative',
      message: `${t('ERROR_ASSIGNING_COURSE')}: ${error?.message}`,
    });
  } else {
    $q.notify({
      type: 'positive',
      message: `${t('COURSE_ASSIGNED_SUCCESSFULLY')}`,
    });
  }

  if (success && data?.documentId) {
    await router.push({
      name: 'course.sections',
      params: { userCourseDocId: data.documentId },
    });
  }
}

async function onLoad(
  index: number,
  done: (stop: boolean) => void,
): Promise<void> {
  // if (entry.isIntersecting) {
  console.log('CourseStore: Intersection detected, index:', index);

  // If intersection is detected, load more courses
  // Check if there are more courses to load
  // and if interception is still visible
  // If there are more courses, call fetchCourseStore
  // Otherwise, do nothing

  await fetchCourseStore();
  if (!courseStorePagination.value?.isLast) {
    console.log('Loading more courses');
  } else {
    console.log('No more courses to load');
    done(true);
  }
}

const handleSelected = (userCourseDocId?: string | null) => {
  const selectedUserCourse = userCourseDocId
    ? courses.value.find(
        (course) =>
          getUserCourseByCourse(course)?.documentId === userCourseDocId,
      )
    : null;

  if (isCourseLocked(selectedUserCourse?.is_free)) {
    $q.notify({
      type: 'warning',
      message: t('LICENSE_REQUIRED_FOR_PREMIUM'),
    });
    return;
  }

  if (!userCourseDocId) {
    console.error('handleSelected: No userCourseDocId provided');
    return;
  }
  void router.push({ name: 'course.sections', params: { userCourseDocId } });
};
</script>

<template>
  <section :class="{ fit: courses.length > 0 }">
    <div class="full-height overflow-auto">
      <div v-if="(courseStorePagination?.start || 0) > 0 && !courses.length">
        <q-banner class="text-center q-mt-lg">
          <p class="title">{{ $t('NO_COURSES_AVAILIABLE_IN_STORE') }}</p>
        </q-banner>
      </div>
      <q-infinite-scroll
        :offset="10"
        @load="onLoad"
      >
        <div class="row wrap q-row-gutter-md q-col-gutter-md q-pb-xl q-mt-xs">
          <div
            v-for="course in courses"
            :key="course.id"
            class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 row justify-center"
          >
            <CourseCard
              v-if="course?.id"
              class="my-card shadow-2 column justify-between fit"
              tag="article"
              style="
                min-height: 260px;
                max-width: 340px;
                border-radius: 18px;
                overflow: hidden;
              "
              bordered
              :image="course?.image?.url ?? courseCardHeaderBgData?.url"
              :name="course?.course_name"
              :subtitle="course?.subtitle"
              :description="course?.description"
              :is-free="course?.is_free"
              :has-favorite="false"
              :action-button-icon="mdiFileDocumentArrowRightOutline"
              @action:click="
                handleSelected(getUserCourseByCourse(course)?.documentId)
              "
            >
              <template #action-button>
                <BButton
                  :label="
                    !isOwnedCourse(course)
                      ? isCourseLocked(course?.is_free)
                        ? $t('LOCKED_PREMIUM_COURSE')
                        : $t('TAKE_COURSE')
                      : isCourseLocked(course?.is_free)
                        ? $t('LOCKED_PREMIUM_COURSE')
                        : $t('GO_TO_MY_COURSE')
                  "
                  text-color="dark"
                  class="text-weight-bolder full-width"
                  color="secondary"
                  size="custom"
                  :disable="isCourseLocked(course?.is_free)"
                  @click.stop="
                    isOwnedCourse(course)
                      ? handleSelected(
                          getUserCourseByCourse(course)?.documentId,
                        )
                      : handleActionClick(course.id)
                  "
                />
              </template>
            </CourseCard>
          </div>
        </div>
        <template #loading>
          <div class="row justify-center q-pa-md">
            <q-spinner
              size="40px"
              color="primary"
            />
          </div>
        </template>
      </q-infinite-scroll>
    </div>
  </section>
</template>
